import * as React from 'react';
import { Omit } from '../../utilities/Omit';
import { IItemCache } from '../../models/IItemCacheObserver';
import { IColumn } from 'office-ui-fabric-react/lib/DetailsList';
import { connectToItemCache, connectToItemCacheLinkFacet } from '../decorators/connectToItemCache';
import { IFacetMapping, IFaceted } from '../../models/Facet';
import { IItemColumn } from '../../models/schema/IItemColumn';
import { itemColumnRenderControl, IItemColumnRendererProps } from '../../models/schema/itemColumnRenderControl';
import { IDecoratedProps } from '../../utilities/components/Decorators';

/**
 * This enum represents default min/max width of a given column.
 * @public
 */
export enum ColumnWidth {
  iconMin = 20,
  iconMax = 20,
  regularMin = 90,
  regularMax = 130,
  displayNameMin = 180,
  displayNameMax = 280,
  sharedMax = 150
}

/**
 * @public
 */
export interface IMapItemCacheToColumnRendererProps extends IItemColumnRendererProps {
  index?: number;
  column?: IColumn;
}

/**
 * @public
 */
export interface IColumnRendererPropMapper<TProps> {
  (props: IMapItemCacheToColumnRendererProps): TProps;
}

/**
 * @public
 */
export function createColumn<TProps>(
  {
    data: {
      ...data
    } = {},
    ...column
  }: Omit<IColumn, 'onRender'>,
  component: React.ComponentType<TProps>
): (getProps: (itemCache: IItemCache, itemKey: string) => (TProps | IColumnRendererPropMapper<TProps>)) => IItemColumn {
  const connectToItemCacheLink = connectToItemCacheLinkFacet.unpack(component as {} as IFaceted);
  const Component = connectToItemCacheLink ?
    connectToItemCacheLink.component as {} as React.ComponentType<TProps> :
    component;

  return (getProps: (itemCache: IItemCache, itemKey: string) => (TProps | IColumnRendererPropMapper<TProps>)) => {
    const Renderer = connectToItemCache((itemCache: IItemCache, props: IMapItemCacheToColumnRendererProps & {
      itemKey: string;
    }) => {
      const {
        itemKey,
        ...mapperProps
      } = props;

      const result = getProps(itemCache, itemKey);

      const componentProps = connectToItemCacheLink ? connectToItemCacheLink.mapItemCacheToProps(itemCache, props) : {};

      const rendererProps = typeof result === 'function' ?
        result({
          ...mapperProps,
          key: itemKey
        }) :
        result;

      return {
        ...componentProps,
        ...(rendererProps as {})
      };
    })(component) as {} as React.ComponentType<IDecoratedProps<{}, {}, IMapItemCacheToColumnRendererProps & {
      itemKey: string;
    }>>;

    return {
      ...column,
      data: {
        ...data,
        renderHandler: (
          itemCache: IItemCache,
          props: typeof itemColumnRenderControl.inputType
        ): typeof itemColumnRenderControl.returnType => {
          const {
            itemKey
          } = props;

          const result = getProps(itemCache, itemKey);

          const componentProps = connectToItemCacheLink ? connectToItemCacheLink.mapItemCacheToProps(itemCache, {
            itemKey
          }) : {};

          const mapper = typeof result === 'function' ? result : (item: IMapItemCacheToColumnRendererProps) => {
            return result;
          };

          return {
            renderer: (item: IItemColumnRendererProps, index?: number, finalColumn?: IColumn) => {
              const rendererProps = mapper({
                ...item,
                index,
                column: finalColumn
              });

              return (
                <Component
                  { ...componentProps }
                  { ...rendererProps }
                />
              );
            }
          };
        }
      },
      onRender: (item: IItemColumnRendererProps, index?: number, finalColumn?: IColumn) => {
        return (
          <Renderer
            itemKey={ item.key }
            { ...item }
            index={ index }
            column={ finalColumn }
          />
        );
      }
    };
  };
}

/**
 * @public
 */
export function createFacetColumn<TProps, TFacets extends object>(
  column: Omit<IColumn, 'onRender'>,
  mapping: IFacetMapping<TFacets>,
  component: React.ComponentType<TProps>): (getProps: (facets: Partial<TFacets>) => TProps) => IItemColumn {
  return (getProps: (facets: Partial<TFacets>) => TProps) => {
    return createColumn(column, component)((itemCache: IItemCache, itemKey: string) => {
      const facets = itemCache.demandItem(mapping, itemKey);

      return getProps(facets);
    });
  };
}
